import useAdminToast from '../useAdminToast';
import ToolAccessList from '../ToolAccessList';
import AdminPagination from '../AdminPagination';
import AdminState from '../AdminState';
import { ChevronDownIcon, CloseIcon, RepeatIcon } from '../AdminIcon';

// Components/Tabs/AdminUserDetailsTab.jsx

import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import Slider from "../Slider/Slider";
import Backend from "../../../utils/api.js";
import { Box, Text, VStack, HStack, Badge, Button, Divider, useDisclosure, Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton, IconButton, Tabs, TabList, TabPanels, Tab, TabPanel, Table, Thead, Tbody, Tr, Th, Td, Select, FormControl, FormLabel } from '@chakra-ui/react';

import "./AdminUserDetailsTab.css";

/**
 * AdminUserDetailsTab Component
 * 
 * Displays detailed information about a user including:
 * - Basic info (name, email, UID, etc.)
 * - Badge connection status and administrative unlinking
 * - Machine access status for the current lab
 * - Swipe in/out history
 */
function AdminUserDetailsTab({ user, targetUser, labId, pushTab, revert }) {
  const toast = useAdminToast();
  const [userDetails, setUserDetails] = useState(null);
  const [machineAccess, setMachineAccess] = useState([]);
  const [swipeHistory, setSwipeHistory] = useState([]);
  const [swipePage, setSwipePage] = useState(1);
  const [swipeTotalPages, setSwipeTotalPages] = useState(1);
  const [swipeTotal, setSwipeTotal] = useState(0);
  const [loadingSwipes, setLoadingSwipes] = useState(false);
  const [visitCount, setVisitCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [swipeError, setSwipeError] = useState(false);
  const [resetError, setResetError] = useState(null);
  const [unknownBadgesError, setUnknownBadgesError] = useState(false);
  const [resettingBadges, setResettingBadges] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [badges, setBadges] = useState([]);
  const [loadingBadges, setLoadingBadges] = useState(false);
  const [badgeError, setBadgeError] = useState(null);
  const [unknownBadges, setUnknownBadges] = useState([]);
  const [loadingUnknownBadges, setLoadingUnknownBadges] = useState(false);
  const [selectedUnknownBadge, setSelectedUnknownBadge] = useState(null);
  const cancelPurgeRef = useRef(null);
  // Map of badge entity ID -> badge name (for efficient lookup)
  const [badgeNameMap, setBadgeNameMap] = useState({});

  const { isOpen: isResetOpen, onOpen: onResetOpen, onClose: onResetClose } = useDisclosure();

  useEffect(() => {
    loadUserData();
  }, [targetUser.UID]);

  const loadUserData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Load user details, machine access, and swipe history in parallel
      const [detailsRes, machineRes, swipeRes] = await Promise.all([
        Backend.adminGetUserDetails(user.UID, targetUser.UID, labId),
        Backend.adminGetUserMachineAccess(user.UID, targetUser.UID, labId),
        Backend.adminGetUserSwipeHistory(user.UID, targetUser.UID, 1, 50, labId),
      ]);

      if ([detailsRes, machineRes, swipeRes].some(response => response.status !== 200)) {
        setError("Pinpoint could not load this user's details, tool access, and visit history. Try again.");
        return;
      }

      if (detailsRes.status === 200) {
        // Handle response wrapped in 'data' envelope from marshal_with
        const userDetailsData = detailsRes.data || detailsRes;
        setUserDetails(userDetailsData);
        
        // Check hasBadgrAuth from the correct location (data envelope or direct)
        const hasBadgrAuth = userDetailsData.hasBadgrAuth;
        
        if (machineRes.status === 200) {
          setMachineAccess(machineRes.items || []);
          setVisitCount(machineRes.visit_count || 0);
          // Fetch badge names asynchronously if user has Badgr auth
          if (hasBadgrAuth) {
            fetchBadgeNamesForMachineAccess(machineRes.items || []);
          }
        }

        if (swipeRes.status === 200) {
          setSwipeHistory(swipeRes.items || []);
          setSwipeTotalPages(swipeRes.pages || 1);
          setSwipeTotal(swipeRes.total || 0);
          setSwipePage(1);
        }

        // Load badges if user has Badgr authentication
        if (hasBadgrAuth) {
          loadBadges();
          loadUnknownBadges();
        } else {
          setBadges([]);
          setBadgeError(null);
          setUnknownBadges([]);
          setUnknownBadgesError(false);
          setSelectedUnknownBadge(null);
          setBadgeNameMap({});
        }
      } else {
        // Handle case where detailsRes doesn't have status 200
        if (machineRes.status === 200) {
          setMachineAccess(machineRes.items || []);
          setVisitCount(machineRes.visit_count || 0);
        }

        if (swipeRes.status === 200) {
          setSwipeHistory(swipeRes.items || []);
          setSwipeTotalPages(swipeRes.pages || 1);
          setSwipeTotal(swipeRes.total || 0);
          setSwipePage(1);
        }
      }
    } catch (err) {
      console.error("Error loading user data:", err);
      setError("Failed to load user data");
    } finally {
      setLoading(false);
    }
  };

  const loadSwipePage = async (pageNum) => {
    setLoadingSwipes(true);
    setSwipeError(false);
    try {
      const swipeRes = await Backend.adminGetUserSwipeHistory(user.UID, targetUser.UID, pageNum, 50, labId);
      if (swipeRes.status === 200) {
        setSwipeHistory(swipeRes.items || []);
        setSwipeTotalPages(swipeRes.pages || 1);
        setSwipeTotal(swipeRes.total || 0);
        setSwipePage(pageNum);
      } else {
        setSwipeError(true);
      }
    } catch (err) {
      setSwipeError(true);
    } finally {
      setLoadingSwipes(false);
    }
  };

  const fetchBadgeNamesForMachineAccess = async (machines) => {
    // Collect all unique badge entity IDs from machine access reasons
    const badgeEntityIds = new Set();
    
    machines.forEach((machine) => {
      if (!machine.canAccess && machine.reasons) {
        machine.reasons.forEach((reason) => {
          if (reason.type === 'NO_BADGE' && reason.requiredBadgeEntityIds) {
            reason.requiredBadgeEntityIds.forEach((entityId) => {
              badgeEntityIds.add(entityId);
            });
          }
        });
      }
    });

    // If no badges to fetch, return early
    if (badgeEntityIds.size === 0) {
      return;
    }

    // Fetch all badge names in parallel (only one call per unique badge)
    const fetchPromises = Array.from(badgeEntityIds).map(async (entityId) => {
      try {
        const badgeInfo = await Backend.apiCallV2(
          `/badges/classes/${entityId}`,
          'GET'
        );

        if (badgeInfo && badgeInfo.status === 200 && badgeInfo.name) {
          // Update the map with the badge name
          setBadgeNameMap((prev) => ({
            ...prev,
            [entityId]: badgeInfo.name,
          }));
        } else {
          // If fetch fails, keep entity ID as fallback
          setBadgeNameMap((prev) => ({
            ...prev,
            [entityId]: entityId,
          }));
        }
      } catch (err) {
        console.error(`Error fetching badge name for ${entityId}:`, err);
        // On error, use entity ID as fallback
        setBadgeNameMap((prev) => ({
          ...prev,
          [entityId]: entityId,
        }));
      }
    });

    // Wait for all fetches to complete (but don't block the UI)
    Promise.all(fetchPromises).catch((err) => {
      console.error('Error fetching some badge names:', err);
    });
  };

  const loadBadges = async () => {
    setLoadingBadges(true);
    setBadgeError(null);
    try {
      // earned_badges returns {data: [badge dicts with name], authenticated}.
      // tool_permissions (used here previously) returns bare tool IDs, which
      // the old name-filter always discarded -- so every user showed as
      // "connected but no badges earned", including users who weren't linked.
      const badgeRes = await Backend.getUserEarnedBadges(targetUser.UID);
      if (badgeRes.status === 200 && badgeRes.authenticated === false) {
        // No V2 badge-provider link (V1-only users land here too: V1 tokens
        // no longer grant access).
        setBadges([]);
        setBadgeError("not_authenticated");
      } else if (badgeRes.status === 200) {
        const badgeList = (badgeRes.data || []).filter(item => item && item.name);
        setBadges(badgeList);
        if (badgeList.length === 0) {
          setBadgeError("no_badges");
        }
      } else if (badgeRes.status === 401) {
        // User not authenticated with Parchment Digital Badges or token expired
        setBadges([]);
        setBadgeError("not_authenticated");
      } else {
        setBadges([]);
        setBadgeError("api_error");
      }
    } catch (err) {
      console.error("Error loading badges:", err);
      setBadges([]);
      // Check if it's a JSON parse error or other error
      if (err instanceof SyntaxError || err.message?.includes('JSON')) {
        setBadgeError("parse_error");
      } else if (err.message?.includes('401') || err.message?.includes('Unauthorized')) {
        setBadgeError("not_authenticated");
      } else {
        setBadgeError("load_error");
      }
    } finally {
      setLoadingBadges(false);
    }
  };

  const loadUnknownBadges = async () => {
    setLoadingUnknownBadges(true);
    setUnknownBadgesError(false);
    try {
      const response = await Backend.adminGetUserUnknownBadges(user.UID, targetUser.UID);
      if (response.status === 200) {
        setUnknownBadges(response.items || []);
      } else {
        setUnknownBadgesError(true);
      }
    } catch {
      setUnknownBadgesError(true);
    } finally {
      setLoadingUnknownBadges(false);
    }
  };

  const handleResetBadges = async () => {
    setResettingBadges(true);
    setResetError(null);
    try {
      const response = await Backend.adminResetUserBadges(user.UID, targetUser.UID);
      if (response.status === 200) {
        // Refresh user details
        await loadUserData();
        onResetClose();
        toast({ title: "Badge connection purged", description: "The user must reconnect their badge account before badge access can be restored." });
      } else {
        setResetError("The badge connection could not be purged. Try again.");
      }
    } catch (err) {
      console.error("Error resetting badges:", err);
      setResetError("The badge connection could not be purged. Try again.");
    } finally {
      setResettingBadges(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadUserData();
    // Badges are loaded in loadUserData if user has Badgr auth
    setRefreshing(false);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    const date = new Date(dateStr);
    return date.toLocaleString();
  };

  if (loading && !userDetails) {
    return (
      <Slider
        name="User Details"
        navColor="rgb(220, 53, 69)"
        slide={true}
        index={0}
        onClick={() => revert(1)}
      >
        <AdminState status="loading" title="Loading user details…" />
      </Slider>
    );
  }

  if (error && !userDetails) return (
    <Slider name="User Details" navColor="rgb(220, 53, 69)" slide={true} index={0} onClick={() => revert(1)}>
      <AdminState status="error" title="User details could not be loaded" description={error} onRetry={handleRefresh} />
    </Slider>
  );

  return (
    <Slider
      name="User Details"
      navColor="rgb(220, 53, 69)"
      slide={true}
      index={0}
      onClick={() => revert(1)}
    >
      <div className={`AdminUserDetailsTab${loading ? ' admin-refreshing' : ''}`} aria-busy={loading}>
        {error && <AdminState status="error" title="User details could not be refreshed" description={error} onRetry={handleRefresh} />}

        {/* User info Header */}
        <Box className="user-header" mb={6}>
          <HStack justify="space-between" align="start" className="admin-card-layout">
            <VStack align="start" spacing={1}>
              <HStack spacing={0} gap={2} flexWrap="wrap">
                <Text fontSize="2xl" fontWeight="bold">
                  {targetUser.firstName} {targetUser.lastName}
                </Text>
                {userDetails?.isSuperAdmin && (
                  <Badge colorScheme="purple" fontSize="sm">Super Admin</Badge>
                )}
              </HStack>
              <Text color="gray.600">{targetUser.email}</Text>
            </VStack>
            <IconButton
              icon={<RepeatIcon />}
              onClick={handleRefresh}
              isLoading={refreshing}
              aria-label="Refresh user data"
              variant="ghost"
            />
          </HStack>
        </Box>

        <Tabs variant="enclosed" colorScheme="blue" display="flex" flexDirection="column">
          <TabList flexShrink={0}>
            <Tab>Overview</Tab>
            <Tab>Tool instance access ({machineAccess.length})</Tab>
            <Tab>Check-in history ({swipeTotal})</Tab>
          </TabList>

          <TabPanels>
            {/* Overview Tab */}
            <TabPanel>
              <VStack align="stretch" spacing={4}>
                {/* Basic Info */}
                <Box className="info-section">
                  <Text as="h3" fontWeight="semibold" fontSize="xl" mb={3}>Basic information</Text>
                  <Table variant="simple" size="sm">
                    <Tbody>
                      <Tr>
                        <Td fontWeight="medium" width="150px">UID</Td>
                        <Td>{targetUser.UID}</Td>
                      </Tr>
                      <Tr>
                        <Td fontWeight="medium">Directory ID</Td>
                        <Td>{userDetails?.directoryID || targetUser.directoryID || "—"}</Td>
                      </Tr>
                      <Tr>
                        <Td fontWeight="medium">Email</Td>
                        <Td>{userDetails?.email || targetUser.email || "—"}</Td>
                      </Tr>
                      <Tr>
                        <Td fontWeight="medium">Super Admin</Td>
                        <Td>{userDetails?.isSuperAdmin ? "Yes" : "No"}</Td>
                      </Tr>
                      <Tr>
                        <Td fontWeight="medium">Visits to {labId}</Td>
                        <Td>{visitCount} visit{visitCount !== 1 ? 's' : ''}</Td>
                      </Tr>
                    </Tbody>
                  </Table>
                </Box>

                <Divider />

                {/* Badges Section */}
                <Box className="info-section">
                  <Text as="h3" fontWeight="semibold" fontSize="xl" mb={3}>Badges</Text>
                  
                  {loadingBadges && badges.length === 0 ? (
                    <AdminState status="loading" title="Loading badges…" mb={0} />
                  ) : userDetails?.hasBadgrAuth && badges.length > 0 ? (
                    <HStack spacing={2} flexWrap="wrap">
                      {badges.map((badge, idx) => (
                        <Badge key={idx} className="admin-entity-badge" colorScheme="purple" fontSize="sm" px={3} py={1}>
                          {badge.name || badge.machineType || 'Badge'}
                        </Badge>
                      ))}
                    </HStack>
                  ) : !userDetails?.hasBadgrAuth || badgeError === "not_authenticated" ? (
                    <AdminState title="Badge account not connected" mb={0}
                      description="The user can connect or reconnect to Parchment Digital Badges from their profile page to display earned badges and use tools that require them." />
                  ) : badgeError && badgeError !== "no_badges" ? (
                    <AdminState status="error" title="Badges could not be loaded" mb={0}
                      description="Pinpoint could not retrieve this user's badges from Parchment Digital Badges. Try again."
                      onRetry={loadBadges} />
                  ) : (
                    <AdminState title="No badges earned yet" mb={0}
                      description="This user is connected to Parchment Digital Badges. Earned training badges will appear here." />
                  )}
                </Box>

                {/* Unknown Badges Dropdown */}
                {userDetails?.hasBadgrAuth && !badgeError && (
                  <Box className="info-section" mt={4}>
                    <FormControl>
                      <FormLabel fontSize="sm" fontWeight="medium" mb={2}>
                        Unmapped badges
                      </FormLabel>
                      {loadingUnknownBadges && unknownBadges.length === 0 ? (
                        <AdminState status="loading" title="Loading unmapped badges…" mb={0} />
                      ) : unknownBadgesError ? (
                        <AdminState status="error" title="Unmapped badges could not be loaded" mb={0}
                          description="Try again to check which badges still need a tool relationship." onRetry={loadUnknownBadges} />
                      ) : unknownBadges.length > 0 ? (
                        <VStack align="stretch" spacing={2}>
                          <Select icon={<ChevronDownIcon />}
                            aria-label="Unmapped badge"
                            placeholder="Select a badge to view details" 
                            size="sm"
                            value={selectedUnknownBadge?.entity_id || ""}
                            onChange={(e) => {
                              const selectedId = e.target.value;
                              const badge = unknownBadges.find(b => b.entity_id === selectedId);
                              setSelectedUnknownBadge(badge || null);
                            }}
                          >
                            {unknownBadges.map((badge, idx) => (
                              <option key={idx} value={badge.entity_id}>
                                {badge.name} ({badge.entity_id})
                              </option>
                            ))}
                          </Select>
                          {selectedUnknownBadge && (
                            <Box 
                              p={3} 
                              bg="gray.50" 
                              borderRadius="md" 
                              border="1px solid" 
                              borderColor="gray.200"
                              maxH="300px"
                              overflowY="auto"
                            >
                              <Text fontSize="xs" fontWeight="bold" mb={2} color="gray.700">
                                Badge JSON:
                              </Text>
                              <Text 
                                as="pre" 
                                fontSize="xs" 
                                fontFamily="mono" 
                                whiteSpace="pre-wrap"
                                wordBreak="break-all"
                                color="gray.800"
                              >
                                {JSON.stringify(selectedUnknownBadge, null, 2)}
                              </Text>
                            </Box>
                          )}
                          <Text fontSize="xs" color="gray.600">
                            These badges exist in the user's Parchment Digital Badges backpack but are not yet mapped to tools in Pinpoint. An admin needs to create badge-to-tool relationships for these badges.
                          </Text>
                        </VStack>
                      ) : (
                        <AdminState title="No unmapped badges" mb={0}
                          description="All retrieved badges have a tool relationship in Pinpoint." />
                      )}
                    </FormControl>
                  </Box>
                )}

                <Divider />

                {/* Badge Connection */}
                <Box className="info-section">
                  <Text as="h3" fontWeight="semibold" fontSize="xl" mb={3}>Parchment Digital Badges Connection</Text>
                  
                  <HStack mb={3} flexWrap="wrap">
                    <Text>Status:</Text>
                    {userDetails?.hasBadgrAuth ? (
                      <Badge colorScheme="green">Connected</Badge>
                    ) : (
                      <Badge colorScheme="gray">Not Connected</Badge>
                    )}
                    {userDetails?.hasBadgrAuthV2 && (
                      <Badge colorScheme="blue" variant="subtle">
                        V2 · {userDetails.badgeIdentityCount} email{userDetails.badgeIdentityCount !== 1 ? 's' : ''}
                      </Badge>
                    )}
                    {userDetails?.hasBadgrAuthV1 && (
                      <Badge colorScheme="orange" variant="subtle">V1 (Legacy)</Badge>
                    )}
                  </HStack>

                  {userDetails?.badgeIdentities?.length > 0 && (
                    <Box mb={3}>
                      <Text fontSize="sm" color="gray.600" mb={1}>
                        Linked emails:
                      </Text>
                      <VStack align="start" spacing={1}>
                        {userDetails.badgeIdentities.map((identity) => (
                          <HStack key={identity.id} spacing={2} flexWrap="wrap">
                            <Text fontSize="sm" fontFamily="mono">{identity.email}</Text>
                            {identity.source === 'admin_manual' && (
                              <Badge colorScheme="purple" variant="outline">Added by admin</Badge>
                            )}
                          </HStack>
                        ))}
                      </VStack>
                      {userDetails.badgeIdentities.some((identity) => identity.emailMasked) && (
                        <Text fontSize="xs" color="gray.600" mt={1}>
                          Non-UMD emails are partially hidden.
                        </Text>
                      )}
                    </Box>
                  )}

                  {user.isSuperAdmin && userDetails?.hasBadgrAuth && (
                    <Button colorScheme="red" variant="solid"
                      onClick={() => { setResetError(null); onResetOpen(); }}>
                      Purge badge connection
                    </Button>
                  )}
                </Box>
              </VStack>
            </TabPanel>

            {/* Machine Access Tab */}
            <TabPanel>
              <Box className="machine-access-section">
                <ToolAccessList instances={machineAccess} labId={labId} badgeNames={badgeNameMap} />
              </Box>
            </TabPanel>

            {/* Swipe history Tab */}
            <TabPanel>
              <Box className="swipe-history-section" tabIndex={0} role="region" aria-label="Check-in history table; scroll horizontally to see more">
                {loadingSwipes && swipeHistory.length === 0 ? (
                  <AdminState status="loading" title="Loading check-in history…" />
                ) : swipeError ? (
                  <AdminState status="error" title="Check-in history could not be loaded" description="Try loading this user's visit history again." onRetry={() => loadSwipePage(swipePage)} />
                ) : swipeHistory.length === 0 ? (
                  <AdminState title="No check-in history found" description="This user's visits to the space will appear here." />
                ) : (
                  <>
                    <Table variant="simple" size="sm">
                      <Thead>
                        <Tr>
                          <Th>Space</Th>
                          <Th>Check-in</Th>
                          <Th>Checkout</Th>
                          <Th>Automatic checkout</Th>
                          <Th>Check-in purpose</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {swipeHistory.map((swipe, idx) => (
                          <Tr key={idx}>
                            <Td>{swipe.space_id}</Td>
                            <Td>{formatDate(swipe.started_at)}</Td>
                            <Td>
                              {swipe.ended_at ? (
                                formatDate(swipe.ended_at)
                              ) : (
                                <Badge colorScheme="green">Active</Badge>
                              )}
                            </Td>
                            <Td>
                              {swipe.ended_at ? "No" : "—"}
                            </Td>
                            <Td>{swipe.reason || "—"}</Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                    <AdminPagination page={swipePage} totalPages={swipeTotalPages} totalItems={swipeTotal} pageSize={50} isLoading={loadingSwipes} onPageChange={loadSwipePage} itemLabel="visits" />
                  </>
                )}
              </Box>
            </TabPanel>
          </TabPanels>
        </Tabs>

        {/* Purge badge connection confirmation */}
        <Modal isOpen={isResetOpen} onClose={() => { if (!resettingBadges) onResetClose(); }}
          initialFocusRef={cancelPurgeRef} closeOnOverlayClick={!resettingBadges} closeOnEsc={!resettingBadges}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Purge badge connection</ModalHeader>
            <ModalCloseButton isDisabled={resettingBadges}><CloseIcon /></ModalCloseButton>
            <ModalBody>
              {resetError && <AdminState status="error" title="Badge connection could not be purged" description={resetError} />}
              <Text>
                Purge the badge connection for{" "}
                <strong>{targetUser.firstName} {targetUser.lastName}</strong>?
              </Text>
              <Text mt={2} color="orange.600" fontSize="sm">
                This forcibly unlinks all of their badge accounts from Pinpoint and removes any legacy
                connection credentials. They will need to reconnect from their profile to use tools
                that require badges. Badges in their provider account are not deleted.
              </Text>
            </ModalBody>
            <ModalFooter>
              <Button ref={cancelPurgeRef} variant="outline" mr={3} onClick={onResetClose} isDisabled={resettingBadges}>
                Cancel
              </Button>
              
              <Button variant="solid"
                colorScheme="red"
                onClick={handleResetBadges}
                isLoading={resettingBadges}
              >
                Purge connection
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </div>
    </Slider>
  );
}

AdminUserDetailsTab.propTypes = {
  user: PropTypes.object.isRequired,
  targetUser: PropTypes.object.isRequired,
  labId: PropTypes.string.isRequired,
  pushTab: PropTypes.func.isRequired,
  revert: PropTypes.func.isRequired,
};

export default AdminUserDetailsTab;
